<template>
  <div class="cron-editor-wrapper">
    <!-- Header del módulo -->
    <div class="module-header mb-4">
      <div class="d-flex align-center">
        <v-icon color="blue-grey darken-2" size="28" class="mr-3">mdi-clock-edit-outline</v-icon>
        <div>
          <p class="text-h6 blue-grey--text text--darken-2 mb-0 font-weight-bold">Editor de Reglas de Disponibilidad</p>
          <p class="text-caption blue-grey--text text--lighten-1 mb-0">
            Configura cuándo los productos son visibles en PDV y comanderos
          </p>
        </div>
      </div>
    </div>

    <v-stepper v-model="currentStep" alt-labels class="elevation-0 step-card">
      <v-stepper-header class="elevation-0 px-0">
        <v-stepper-step :complete="currentStep > 1" step="1" color="blue darken-2">
          Seleccionar Productos
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="currentStep > 2" step="2" color="blue darken-2">
          Configurar Reglas
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step step="3" color="blue darken-2">
          Confirmación
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- ==============================================
             PASO 1: SELECCIONAR PRODUCTOS
             ============================================== -->
        <v-stepper-content step="1" class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <p class="text-caption blue-grey--text mb-3">
                Filtra por categoría o etiqueta y agrega los productos a los que aplicarás la regla.
              </p>

              <!-- Selector de modo: Categoría / Tag -->
              <v-tabs v-model="mode" dense background-color="transparent" color="blue darken-2" class="mb-3">
                <v-tab class="text-capitalize"><v-icon small left>mdi-shape</v-icon> Categoría</v-tab>
                <v-tab class="text-capitalize"><v-icon small left>mdi-tag</v-icon> Etiqueta (Tag)</v-tab>
              </v-tabs>

              <!-- Filtro por Categoría -->
              <v-autocomplete v-if="mode == 0" v-model="categorySelected" :items="Categories"
                label="Filtrar por categoría" item-value="id" item-text="Nombre" outlined dense
                prepend-inner-icon="mdi-filter-outline" clearable hide-details class="mb-3" />

              <!-- Filtro por Tag -->
              <v-text-field v-if="mode == 1" v-model="tag" dense label="Filtrar por etiqueta (tag)" outlined
                prepend-inner-icon="mdi-tag-outline" clearable hide-details class="mb-3" />

              <!-- Selector de Producto -->
              <v-autocomplete v-model="productoSelected" :items="global_prod" item-value="id" item-text="Nombre"
                label="Buscar y seleccionar producto" outlined dense prepend-inner-icon="mdi-magnify" hide-details
                class="mb-2">
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.Nombre }}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip x-small :color="item.Disponible ? 'success' : 'grey'" dark class="mt-1">
                        Regla actual: {{ item.Disponible || 'Sin regla' }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <!-- Botones de acción -->
              <div class="d-flex gap-2 mb-4">
                <v-btn color="blue darken-2" dark small class="flex-grow-1" :disabled="!productoSelected"
                  @click="addProduct">
                  <v-icon small left>mdi-plus</v-icon> Agregar producto
                </v-btn>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="success" outlined small v-bind="attrs" v-on="on" @click="selectAllProducts">
                      <v-icon small>mdi-checkbox-marked-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Seleccionar todos los productos listados</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="red lighten-1" outlined small v-bind="attrs" v-on="on"
                      :disabled="products.length === 0" @click="clearAllProducts">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Vaciar lista de productos seleccionados</span>
                </v-tooltip>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="products-section h-100">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-caption font-weight-bold blue-grey--text text--darken-1">PRODUCTOS
                    SELECCIONADOS</span>
                  <v-chip x-small color="blue darken-2" dark v-if="products.length > 0">{{ products.length }}</v-chip>
                </div>
                <div v-if="products.length === 0" class="empty-state text-center py-6">
                  <v-icon color="blue-grey lighten-3" size="40">mdi-package-variant-closed</v-icon>
                  <p class="text-caption blue-grey--text text--lighten-2 mt-2 mb-0">
                    Aún no has seleccionado productos.<br />Usa el buscador para agregar.
                  </p>
                </div>
                <div v-else class="products-list">
                  <v-list dense class="pa-0">
                    <v-list-item v-for="(product, index) in products" :key="index"
                      class="product-item rounded mb-1 px-2">
                      <v-list-item-icon class="mr-2 my-auto"><v-icon small
                          color="blue darken-1">mdi-package-variant</v-icon></v-list-item-icon>
                      <v-list-item-content class="py-2">
                        <v-list-item-title class="text-body-2 font-weight-medium">{{ product.Nombre
                          }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          Regla actual: <code class="cron-code">{{ product.Disponible || '—' }}</code>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action class="my-auto ml-0">
                        <v-btn icon x-small @click="deleteProduct(index)"><v-icon small
                            color="red lighten-2">mdi-close-circle-outline</v-icon></v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </div>
              </div>
            </v-col>
          </v-row>

        </v-stepper-content>

        <!-- ==============================================
             PASO 2: CONFIGURAR REGLAS
             ============================================== -->
        <v-stepper-content step="2" class="pa-4">
          <v-row>
            <v-col cols="12" md="5">
              <p class="text-caption font-weight-bold mb-2 blue-grey--text text--darken-1">PRODUCTOS A MODIFICAR ({{
                draftProducts.length }})</p>
              <div class="products-list" style="max-height: 400px;">
                <v-card outlined v-for="(product, index) in draftProducts" :key="product.id"
                  class="mb-2 product-card-draft">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <span class="text-body-2 font-weight-medium text-truncate" style="max-width: 80%;">{{
                        product.Nombre
                      }}</span>
                    </div>
                    <!-- Lista de reglas del producto -->
                    <div v-if="product.rules.length === 0" class="text-caption grey--text">Sin reglas configuradas.
                    </div>
                    <div v-for="(rule, rIndex) in product.rules" :key="rIndex"
                      class="rule-row d-flex align-center mb-1">
                      <code class="cron-code flex-grow-1">{{ rule }}</code>
                      <span class="text-caption grey--text ml-2 rule-label">{{ getParsedCron(rule) }}</span>
                      <v-btn icon small class="ml-2"
                        :color="isEditingRule(index, rIndex) ? 'orange darken-1' : 'blue darken-1'"
                        @click="editProductRule(index, rIndex)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small class="ml-1" color="red darken-1" @click="deleteProductRule(index, rIndex)">
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-col>

            <v-col cols="12" md="7">
              <v-card outlined class="h-100 bg-light-form">
                <v-card-title class="pa-3 pb-0">
                  <div class="d-flex flex-column w-100">
                    <div class="d-flex align-center justify-space-between w-100">
                      <span class="text-subtitle-2 font-weight-bold">
                        {{ editingMode ? 'Editando Regla Específica' : 'Agregar Nueva Regla (A todos los productos)' }}
                      </span>
                      <v-btn v-if="editingMode" x-small text color="grey" @click="cancelEditMode">Cancelar
                        Edición</v-btn>
                    </div>
                    <p class="text-caption grey--text mb-0 mt-1" v-if="editingMode">
                      Editando regla de: <strong>{{ draftProducts[editingProductIndex].Nombre }}</strong>
                    </p>
                  </div>
                </v-card-title>
                <v-card-text class="pa-4">

                  <!-- FORM MANUAL PRINCIPAL -->
                  <v-row dense>
                    <v-col cols="12" class="d-flex justify-end mb-1">
                      <v-btn x-small text color="blue darken-1" @click="visualDialog = true">
                        <v-icon x-small left>mdi-magic-staff</v-icon> Generador Visual
                      </v-btn>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-text-field dense outlined label="Minutos" v-model="minutes"
                        prepend-inner-icon="mdi-clock-time-three-outline" hint="Ej: * ó 0 ó 30"
                        persistent-hint></v-text-field>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-text-field dense outlined label="Hora" v-model="hours" prepend-inner-icon="mdi-clock-outline"
                        hint="Ej: * ó 8 ó 20" persistent-hint></v-text-field>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-text-field dense outlined label="Día del mes" v-model="daymonth"
                        prepend-inner-icon="mdi-calendar-month-outline" hint="Ej: * ó 1-15"
                        persistent-hint></v-text-field>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-text-field dense outlined label="Mes" v-model="month"
                        prepend-inner-icon="mdi-calendar-range-outline" hint="Ej: * ó 1 ó 6"
                        persistent-hint></v-text-field>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-text-field dense outlined label="Día de semana" v-model="dayweek"
                        prepend-inner-icon="mdi-calendar-week-outline" hint="Ej: * ó 1-5 ó 0,6"
                        persistent-hint></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Preview de la regla -->
                  <div class="cron-preview-box mt-4 mb-3 pa-3 rounded">
                    <div class="d-flex align-center mb-1">
                      <v-icon small color="blue darken-2" class="mr-1">mdi-eye-outline</v-icon>
                      <span class="text-caption font-weight-bold blue--text text--darken-2">VISTA PREVIA</span>
                    </div>
                    <div class="d-flex align-center flex-wrap">
                      <code class="cron-code-lg mr-3">{{ cronToAdd }}</code>
                      <span class="text-body-2 blue-grey--text text--darken-1">→</span>
                      <span class="text-body-2 blue-grey--text text--darken-2 ml-2 font-weight-medium">{{
                        getParsedCron(cronToAdd) }}</span>
                    </div>
                  </div>

                  <!-- Botón Agregar Regla -->
                  <v-btn :color="editingMode ? 'orange darken-1' : 'green darken-1'" dark block depressed
                    @click="applyRuleDraft()">
                    <v-icon small left>{{ editingMode ? 'mdi-content-save-edit-outline' : 'mdi-plus-circle-outline'
                    }}</v-icon>
                    {{ editingMode ?
                      'Guardar Cambios a la Regla' :
                      'Añadir regla a los ' + draftProducts.length + 'productos'
                    }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- ====== DIALOG: Generador Visual ====== -->
          <v-dialog v-model="visualDialog" max-width="640" scrollable>
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon left color="green darken-2">mdi-magic-staff</v-icon>
                Generador Visual de Cron
                <v-spacer></v-spacer>
                <v-btn icon @click="visualDialog = false"><v-icon>mdi-close</v-icon></v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-4">
                <!-- Plantillas -->
                <div class="mb-3">
                  <p class="text-caption font-weight-bold mb-2 blue-grey--text text--darken-1">PLANTILLAS RÁPIDAS</p>
                  <div class="d-flex flex-wrap gap-2">
                    <v-chip small outlined color="green darken-2"
                      @click="applyTemplate('*', '*', '*', '*', '*')">Siempre</v-chip>
                    <v-chip small outlined color="green darken-2"
                      @click="applyTemplate('*', '*', '*', '*', '1,2,3,4,5')">Lunes
                      a Viernes</v-chip>
                    <v-chip small outlined color="green darken-2"
                      @click="applyTemplate('*', '*', '*', '*', '0,6')">Fines de
                      Semana</v-chip>
                    <v-chip small outlined color="green darken-2"
                      @click="applyTemplate('*', '8,9,10,11,12', '*', '*', '*')">Mañanas (8-12)</v-chip>
                    <v-chip small outlined color="green darken-2"
                      @click="applyTemplate('*', '13,14,15,16,17,18', '*', '*', '*')">Tardes (13-18)</v-chip>
                  </div>
                </div>
                <v-divider class="mb-3"></v-divider>
                <p class="text-caption font-weight-bold mb-1 blue-grey--text text--darken-1">DÍAS DE LA SEMANA <span
                    class="text-caption grey--text">(Vacío = Todos los días)</span></p>
                <v-chip-group v-model="selectedDaysWeek" multiple active-class="green darken-2 white--text"
                  class="mb-3">
                  <v-chip v-for="day in daysWeekOptions" :key="day.value" :value="day.value" filter small>{{ day.label
                  }}</v-chip>
                </v-chip-group>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <p class="text-caption font-weight-bold mb-1 blue-grey--text text--darken-1">HORAS <span
                        class="text-caption grey--text">(Vacío = Todo el día)</span></p>
                    <v-autocomplete v-model="selectedHours" :items="hoursOptions" multiple outlined dense small-chips
                      clearable label="Cualquier hora" hide-details></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <p class="text-caption font-weight-bold mb-1 blue-grey--text text--darken-1">MINUTOS <span
                        class="text-caption grey--text">(Vacío = Cualquier min)</span></p>
                    <v-autocomplete v-model="selectedMinutes" :items="minutesOptions" multiple outlined dense
                      small-chips clearable label="Cualquier minuto" hide-details></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" class="mt-2">
                    <p class="text-caption font-weight-bold mb-1 blue-grey--text text--darken-1">DÍA DEL MES</p>
                    <v-autocomplete v-model="selectedDaysMonth" :items="daysMonthOptions" multiple outlined dense
                      small-chips clearable label="Todos los días" hide-details></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" class="mt-2">
                    <p class="text-caption font-weight-bold mb-1 blue-grey--text text--darken-1">MESES</p>
                    <v-autocomplete v-model="selectedMonths" :items="monthsOptions" multiple outlined dense small-chips
                      clearable label="Todos los meses" hide-details></v-autocomplete>
                  </v-col>
                </v-row>

                <!-- Preview dentro del dialog -->
                <div class="cron-preview-box mt-4 pa-3 rounded">
                  <div class="d-flex align-center mb-1">
                    <v-icon small color="blue darken-2" class="mr-1">mdi-eye-outline</v-icon>
                    <span class="text-caption font-weight-bold blue--text text--darken-2">RESULTADO GENERADO</span>
                  </div>
                  <code class="cron-code-lg">{{ cronFromVisual }}</code>
                  <span class="text-body-2 blue-grey--text text--darken-2 ml-2">{{ getParsedCron(cronFromVisual)
                  }}</span>
                </div>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="visualDialog = false">Cancelar</v-btn>
                <v-btn color="green darken-1" dark depressed @click="applyCronFromVisual">
                  <v-icon left>mdi-check</v-icon> Usar este cron
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

        </v-stepper-content>

        <!-- ==============================================
             PASO 3: CONFIRMACIÓN Y GUARDADO
             ============================================== -->
        <v-stepper-content step="3" class="pa-4">
          <v-card outlined class="bg-light-form mb-4">
            <v-card-text class="pa-4 text-center">
              <v-icon size="48" color="primary" class="mb-2">mdi-check-all</v-icon>
              <h3 class="text-h6 font-weight-bold blue-grey--text text--darken-2">Confirmación Final</h3>
              <p class="text-body-2 blue-grey--text text--darken-1">
                A continuación se muestra el resultado final de las reglas que tendrán los <strong>{{
                  draftProducts.length
                  }}</strong> productos seleccionados. Revisa que todo esté correcto antes de guardar en la base de
                datos.
              </p>
            </v-card-text>
          </v-card>

          <v-simple-table dense class="elevation-1 rounded">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Producto</th>
                  <th class="text-left">Regla Final (Crons)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in draftProducts" :key="product.id">
                  <td>{{ product.Nombre }}</td>
                  <td>
                    <div v-if="product.rules.length === 0" class="grey--text text-caption">Sin regla</div>
                    <code v-else class="cron-code" style="font-size: 13px;">{{ product.rules.join(';') }}</code>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

        </v-stepper-content>
      </v-stepper-items>

      <!-- Footer Sticky global para los botones de navegación -->
      <div class="stepper-footer-sticky px-4 py-3">
        <!-- Paso 1 -->
        <template v-if="currentStep === 1">
          <div></div>
          <v-btn color="primary" depressed @click="initDraftsAndNextStep" :disabled="products.length === 0">
            Siguiente paso: Configurar Reglas <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </template>

        <!-- Paso 2 -->
        <template v-if="currentStep === 2">
          <v-btn text @click="currentStep = 1"><v-icon left>mdi-arrow-left</v-icon> Volver a Productos</v-btn>
          <v-btn color="primary" depressed @click="currentStep = 3">
            Siguiente paso: Confirmación <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </template>

        <!-- Paso 3 -->
        <template v-if="currentStep === 3">
          <v-btn text @click="currentStep = 2" :disabled="isSaving"><v-icon left>mdi-arrow-left</v-icon> Editar
            Reglas</v-btn>
          <v-btn color="success" x-large depressed @click="saveRulesToProducts" :loading="isSaving">
            <v-icon left>mdi-content-save-outline</v-icon> Confirmar y Guardar en BD
          </v-btn>
        </template>
      </div>
    </v-stepper>
  </div>
</template>

<script>
const cronParser = require("cron-parser");
import { parseHumanReadable } from "cron-js-parser";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "CronRulesEditor",
  data() {
    return {
      currentStep: 1,
      mode: 0,
      tag: "",
      Categories: [],
      products: [],
      draftProducts: [],
      productoSelected: null,
      categorySelected: "ALL",

      // Editing context
      editingMode: false,
      editingProductIndex: null,
      editingRuleIndex: null,

      // Datos Modo Experto
      minutes: "*",
      hours: "*",
      daymonth: "*",
      month: "*",
      dayweek: "*",
      // Datos Modo Visual
      ruleType: 'easy',
      showAdvancedVisual: false,
      selectedDaysWeek: [],
      selectedHours: [],
      selectedMinutes: [],
      selectedDaysMonth: [],
      selectedMonths: [],
      daysWeekOptions: [
        { label: 'Dom', value: '0' },
        { label: 'Lun', value: '1' },
        { label: 'Mar', value: '2' },
        { label: 'Mié', value: '3' },
        { label: 'Jue', value: '4' },
        { label: 'Vie', value: '5' },
        { label: 'Sáb', value: '6' },
      ],
      monthsOptions: [
        { text: 'Ene', value: '1' }, { text: 'Feb', value: '2' }, { text: 'Mar', value: '3' },
        { text: 'Abr', value: '4' }, { text: 'May', value: '5' }, { text: 'Jun', value: '6' },
        { text: 'Jul', value: '7' }, { text: 'Ago', value: '8' }, { text: 'Sep', value: '9' },
        { text: 'Oct', value: '10' }, { text: 'Nov', value: '11' }, { text: 'Dic', value: '12' }
      ],
      isSaving: false,
      visualDialog: false,
    };
  },
  methods: {
    ...mapActions("Categorias", { fetchCategories: "getCategories" }),
    getParsedCron(expression) {
      return parseHumanReadable(expression, {}, "es");
    },
    clearAndValidate(param) {
      const clearValue = param.replace(/\s/g, "");
      if (clearValue == "") {
        return "*";
      }
      return clearValue;
    },
    addProduct() {
      let exist = this.$pgsHelper.getById(this.global_prod, "id", this.productoSelected);
      if (exist && !this.products.some(p => p.id === exist.id)) {
        this.products.push(exist);
      }
      this.productoSelected = null;
    },
    selectAllProducts() {
      this.products = [...this.global_prod];
    },
    clearAllProducts() {
      this.products = [];
    },
    parseDisponibleRules(rawDisponible) {
      if (!rawDisponible) return [];
      // Si es un array, usarlo directamente
      if (Array.isArray(rawDisponible)) {
        return rawDisponible.map(r => String(r).trim()).filter(r => r !== '');
      }
      // Si es string, separar por ';' o por ','
      if (typeof rawDisponible === 'string') {
        const separator = rawDisponible.includes(';') ? ';' : ',';
        return rawDisponible.split(separator).map(r => r.trim()).filter(r => r !== '');
      }
      return [];
    },
    initDraftsAndNextStep() {
      // Crear el draft con rules vacío primero para que Vue 2 registre la propiedad
      this.draftProducts = this.products.map(p => ({ ...p, rules: [] }));

      // Usar $set para garantizar reactividad al asignar las reglas reales
      this.products.forEach((p, i) => {
        this.$set(this.draftProducts[i], 'rules', this.parseDisponibleRules(p.Disponible));
      });

      this.cancelEditMode();
      this.currentStep = 2;
    },
    deleteProduct(index) {
      this.products.splice(index, 1);
    },
    deleteProductRule(productIndex, ruleIndex) {
      this.draftProducts[productIndex].rules.splice(ruleIndex, 1);
      // Si justo estaba editando esta regla, cancelar edición
      if (this.editingMode && this.editingProductIndex === productIndex && this.editingRuleIndex === ruleIndex) {
        this.cancelEditMode();
      }
    },
    editProductRule(productIndex, ruleIndex) {
      this.editingMode = true;
      this.editingProductIndex = productIndex;
      this.editingRuleIndex = ruleIndex;
      this.loadRuleToForm(this.draftProducts[productIndex].rules[ruleIndex]);
    },
    isEditingRule(productIndex, ruleIndex) {
      return this.editingMode && this.editingProductIndex === productIndex && this.editingRuleIndex === ruleIndex;
    },
    cancelEditMode() {
      this.editingMode = false;
      this.editingProductIndex = null;
      this.editingRuleIndex = null;
      this.applyTemplate('*', '*', '*', '*', '*');
      this.ruleType = 'easy';
    },
    loadRuleToForm(cronString) {
      // Intentar cargar en modo experto directamente para asegurar compatibilidad
      const parts = cronString.split(' ');
      if (parts.length === 5) {
        this.ruleType = 'expert';
        this.minutes = parts[0];
        this.hours = parts[1];
        this.daymonth = parts[2];
        this.month = parts[3];
        this.dayweek = parts[4];
      }
    },
    applyCronFromVisual() {
      // Copiar el cron generado visualmente al form manual
      const parts = this.cronFromVisual.split(' ');
      this.minutes = parts[0] || '*';
      this.hours = parts[1] || '*';
      this.daymonth = parts[2] || '*';
      this.month = parts[3] || '*';
      this.dayweek = parts[4] || '*';
      this.visualDialog = false;
    },
    applyRuleDraft() {
      const cron = this.cronToAdd;
      if (this.editingMode) {
        // Actualizar solo al producto que estamos editando
        this.draftProducts[this.editingProductIndex].rules.splice(this.editingRuleIndex, 1, cron);
        this.cancelEditMode();
        this.$toast.success(" ", "Regla modificada en el borrador");
      } else {
        // Añadir regla a TODOS los productos en draft
        this.draftProducts.forEach(p => {
          if (!p.rules.includes(cron)) {
            p.rules.push(cron);
          }
        });
        this.$toast.success(" ", "Regla agregada a todos los productos");
      }
    },
    applyTemplate(min, hr, dm, m, dw) {
      const parseToArray = (val) => val === '*' ? [] : val.split(',');
      this.selectedMinutes = parseToArray(min);
      this.selectedHours = parseToArray(hr);
      this.selectedDaysMonth = parseToArray(dm);
      this.selectedMonths = parseToArray(m);
      this.selectedDaysWeek = parseToArray(dw);
    },
    async saveRulesToProducts() {
      this.isSaving = true;
      try {
        let promises = [];
        let dataSend = {
          productos: []
        }
        this.draftProducts.forEach(product => {
          dataSend.productos.push({
            id: product.id,
            Disponible: product.rules
          })
        });

        console.log(dataSend)
        return false
        /*this.$toast.success(" ", "Reglas aplicadas correctamente a la base de datos");

        // Limpiar para nueva operación y volver al paso 1
        this.products = [];
        this.draftProducts = [];
        this.productoSelected = null;
        this.currentStep = 1;*/
      } catch (error) {
        console.error(error);
        this.$toast.error(" ", "Error al guardar las reglas");
      } finally {
        this.isSaving = false;
      }
    },
    async getCategories() {
      await this.fetchCategories();
      const claims = this.$route.params?.user?.tokenResult?.claims;
      const cats = this.getCategoriesFiltered(claims);
      this.Categories = [{ id: "ALL", Nombre: "TODAS" }, ...cats];
    },
  },
  computed: {
    ...mapGetters("Categorias", ["getCategoriesFiltered"]),
    hoursOptions() {
      return Array.from({ length: 24 }, (_, i) => ({ text: `${i}:00`, value: `${i}` }));
    },
    minutesOptions() {
      return Array.from({ length: 60 }, (_, i) => ({ text: `${i} min`, value: `${i}` }));
    },
    daysMonthOptions() {
      return Array.from({ length: 31 }, (_, i) => ({ text: `Día ${i + 1}`, value: `${i + 1}` }));
    },
    cronToAdd() {
      // Modo principal: siempre manual
      return (
        this.clearAndValidate(this.minutes) + " " +
        this.clearAndValidate(this.hours) + " " +
        this.clearAndValidate(this.daymonth) + " " +
        this.clearAndValidate(this.month) + " " +
        this.clearAndValidate(this.dayweek)
      );
    },
    cronFromVisual() {
      // Computed exclusivo del generador visual en el dialog
      const formatPart = (arr) => arr && arr.length > 0 ? arr.sort((a, b) => parseInt(a) - parseInt(b)).join(',') : '*';
      return (
        formatPart(this.selectedMinutes) + " " +
        formatPart(this.selectedHours) + " " +
        formatPart(this.selectedDaysMonth) + " " +
        formatPart(this.selectedMonths) + " " +
        formatPart(this.selectedDaysWeek)
      );
    },
    global_prod() {
      let prod = this.$store.getters.getProductos;
      let productos = prod;
      let prodPrices = [];

      productos.forEach((producto) => {
        if (
          producto.Tipo == "Producto" || producto.Tipo == "Servicio" ||
          producto.Tipo == "Producto-SSal" ||
          (producto.Tipo == "AYB-Producto" && !producto.Dlat)
        ) {
          if (this.mode == 0) {
            if (this.categorySelected != "ALL") {
              if (producto.Categoria == this.categorySelected) {
                prodPrices.push({ Origen: "", Price: producto.Precio, NombreOrigen: "", ...producto });
              }
            } else {
              prodPrices.push({ Origen: "", Price: producto.Precio, NombreOrigen: "", ...producto });
            }
          } else if (this.mode == 1) {
            if (this.tag != "") {
              if (producto.tags && producto.tags.includes(this.tag)) {
                prodPrices.push({ Origen: "", Price: producto.Precio, NombreOrigen: "", ...producto });
              }
            } else {
              prodPrices.push({ Origen: "", Price: producto.Precio, NombreOrigen: "", ...producto });
            }
          }
        }
      });

      return this.$_.orderBy(prodPrices, "Nombre", "asc");
    },
  },
  mounted() {
    this.getCategories();
  },
};
</script>

<style scoped>
.cron-editor-wrapper {
  padding: 8px 0;
  padding-bottom: 80px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 160px);
}

.module-header {
  border-bottom: 1px solid #eceff1;
  padding-bottom: 12px;
}

.step-card {
  border-radius: 12px !important;
  border-color: #cfd8dc !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.products-list {
  max-height: 380px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #b0bec5 transparent;
}

.products-list::-webkit-scrollbar {
  width: 4px;
}

.products-list::-webkit-scrollbar-thumb {
  background-color: #b0bec5;
  border-radius: 4px;
}

.product-item {
  background-color: #f5f7f9;
  border: 1px solid #eceff1;
  min-height: 48px !important;
}

.product-card-draft {
  transition: all 0.2s ease;
}

.product-card-draft:hover {
  background-color: #f8f9fa;
  border-color: #cfd8dc;
}

.empty-state {
  border: 2px dashed #cfd8dc;
  border-radius: 8px;
  background-color: #fafbfc;
}

.bg-light-form {
  background-color: #f5f7f9;
}

.cron-preview-box {
  background: linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%);
  border: 1px solid #90caf9;
}

.cron-code {
  font-family: "Courier New", Courier, monospace;
  font-size: 11px;
  background-color: rgba(0, 0, 0, 0.06);
  padding: 1px 5px;
  border-radius: 4px;
  color: #37474f;
}

.cron-code-lg {
  font-family: "Courier New", Courier, monospace;
  font-size: 14px;
  font-weight: bold;
  background-color: rgba(25, 118, 210, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  color: #1565c0;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.pointer {
  cursor: pointer;
}

/* Footer fijo en el bottom de la pantalla */
.stepper-footer-sticky {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border-top: 1px solid #cfd8dc;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.10);
  padding: 12px 24px;
}

/* Fila de regla individual en Paso 2 */
.rule-row {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 4px 8px;
}

.rule-label {
  font-size: 11px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
